import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { logEvent } from '../services/analytics/eventLogger';
import { useSession } from './SessionContext';
import { getUserTier, TIER_ANON, TIER_SIMPLE, FULL_ONLY_SCREENS } from '../utils/userTier';
import { clearPinOnNavigate } from '../services/people/reconnectPin';

// Create the Navigation Context
const NavigationContext = createContext();

/**
 * Navigation Provider Component
 *
 * Provides a centralized navigation system for the app that preserves
 * component hierarchy and WebSocket connections during navigation.
 * Now with separate navigation histories for profile and anonymous users.
 */
export const NavigationProvider = ({
  children,
  initialScreen = 'loading',
  initialParams = {},
  initialQROpen = false,
}) => {
  // Current screen and parameters state
  const [currentScreen, setCurrentScreen] = useState(initialScreen);
  const [screenParams, setScreenParams] = useState(initialParams);

  // QR Overlay state — controls global QR overlay visibility
  const [isQROpen, setIsQROpen] = useState(initialQROpen);

  // Set initial screen once on mount
  useEffect(() => {
    console.log(`NAVIGATION_CONTEXT: Setting initial screen to: ${initialScreen}`);
    setCurrentScreen(initialScreen);
  }, [initialScreen]);

  // Sync QR overlay state when initialQROpen prop arrives after first render
  useEffect(() => {
    if (initialQROpen) {
      setIsQROpen(true);
    }
  }, [initialQROpen]);

  // Separate navigation histories for profile and anonymous users
  const [profileHistory, setProfileHistory] = useState([]);
  const [anonHistory, setAnonHistory] = useState([]);

  // Helper function to determine if the user is a profile user
  // Uses profile_user_id (not profile_user_data) — kept one-key for history-stack
  // selection only (reads and writes hit the same stack either way). The nav
  // GUARD below uses the tier module's two-key predicate — the one-key variant
  // bounced legit Anons carrying a stale id from an aborted conversion.
  const isProfileUser = useCallback(() => {
    return !!localStorage.getItem('profile_user_id');
  }, []);

  // Get the appropriate history and setter based on user type
  const getUserHistory = useCallback(() => {
    const isProfile = isProfileUser();
    console.log(`NAVIGATION_CONTEXT: User type is ${isProfile ? 'profile' : 'anonymous'}`);
    return {
      history: isProfile ? profileHistory : anonHistory,
      setHistory: isProfile ? setProfileHistory : setAnonHistory,
    };
  }, [profileHistory, anonHistory, isProfileUser]);

  /**
   * Navigate to a specific screen with parameters
   * @param {string} screen - The screen to navigate to
   * @param {object} params - Parameters to pass to the screen
   * @param {boolean} replace - If true, replaces current screen in history instead of adding
   */
  const navigateTo = useCallback(
    (screen, params = {}, replace = false) => {
      // Nav guard: Anon stays in Anon, profile-world (Simple + Full) stays in
      // Profile — and within the profile world, Full-only screens (chat list,
      // Posts) redirect a Simple profile to People. Tier is asked, never
      // re-derived here (userTier.js is the one home for the decision).
      const tier = getUserTier();
      if (tier === TIER_ANON && screen.startsWith('profile-')) {
        console.log(`NAVIGATION: Blocked anon → ${screen}, redirecting to anon-connections-list`);
        logEvent('nav_guard_blocked', {
          attempted: screen,
          redirectedTo: 'anon-connections-list',
          userType: 'anon',
          source: 'navigate_to',
        });
        screen = 'anon-connections-list';
        params = {};
      } else if (tier !== TIER_ANON && screen.startsWith('anon-')) {
        console.log(
          `NAVIGATION: Blocked profile → ${screen}, redirecting to profile-connections-list`
        );
        logEvent('nav_guard_blocked', {
          attempted: screen,
          redirectedTo: 'profile-connections-list',
          userType: 'profile',
          source: 'navigate_to',
        });
        screen = 'profile-connections-list';
        params = {};
      } else if (tier === TIER_SIMPLE && FULL_ONLY_SCREENS.has(screen)) {
        console.log(
          `NAVIGATION: Blocked simple → ${screen}, redirecting to profile-connections-list`
        );
        logEvent('nav_guard_blocked', {
          attempted: screen,
          redirectedTo: 'profile-connections-list',
          userType: 'simple',
          source: 'navigate_to',
        });
        screen = 'profile-connections-list';
        params = {};
      }

      console.log(`NAVIGATION_TRACE: [${new Date().toISOString()}] navigateTo called`);
      console.log(`NAVIGATION_TRACE: Request to navigate from '${currentScreen}' to '${screen}'`);
      console.log('NAVIGATION_TRACE: Current params:', JSON.stringify(screenParams));
      console.log('NAVIGATION_TRACE: New params:', JSON.stringify(params));
      console.log('NAVIGATION_TRACE: Stack trace:', new Error().stack);

      // Enhanced debug for navigation events
      console.log('NAVIGATION_TRACE: Current URL:', window.location.href);
      console.log('NAVIGATION_TRACE: Room ID in params:', params.chatRoomId);

      // Get the appropriate history and setter based on user type
      const { history, setHistory } = getUserHistory();

      // Debug navigation history
      console.log(
        'NAVIGATION_TRACE: Current history stack:',
        history.map((h) => `${h.screen}`).join(' → ')
      );
      console.log(`NAVIGATION_DEBUG: Using ${isProfileUser() ? 'profile' : 'anon'} history stack`);

      // Save current screen to user-specific history for back functionality
      // Unless replace=true, in which case we don't add to history
      if (!replace) {
        setHistory([...history, { screen: currentScreen, params: screenParams }]);
        console.log('NAVIGATION_DEBUG: Added to history');
      } else {
        console.log('NAVIGATION_DEBUG: Replace mode - not adding to history');
      }

      // A reconnect's pin on People holds until you go elsewhere in Royale
      // (SYSTEM §4c-iv) — this is the one door every navigation passes
      clearPinOnNavigate(screen);

      // Update current screen and parameters
      setCurrentScreen(screen);
      setScreenParams(params);

      // Persist to sessionStorage for reload recovery
      sessionStorage.setItem('nav_current_screen', screen);

      // Confirm navigation was processed
      setTimeout(() => {
        console.log('NAVIGATION_DEBUG: Navigation complete. Current screen is now:', screen);
        if (params.chatRoomId) {
          console.log('NAVIGATION_DEBUG: Using chatRoomId:', params.chatRoomId);
        }
      }, 0);
    },
    [currentScreen, screenParams, getUserHistory, isProfileUser]
  );

  /**
   * Go back to the previous screen
   */
  const goBack = useCallback(
    (defaultScreen = 'chat') => {
      console.log(`NAVIGATION_TRACE: [${new Date().toISOString()}] goBack called`);
      console.log('NAVIGATION_TRACE: Stack trace:', new Error().stack);

      // Get the appropriate history and setter based on user type
      const { history, setHistory } = getUserHistory();
      console.log(
        `NAVIGATION_TRACE: Using ${isProfileUser() ? 'profile' : 'anon'} history stack for back navigation`
      );

      if (history.length > 0) {
        // Get the last screen from history
        const prevNavigation = history[history.length - 1];

        console.log(`NAVIGATION_DEBUG: Going back to ${prevNavigation.screen} from history`);
        console.log(
          `NAVIGATION_DEBUG: History stack before pop:`,
          history.map((h) => h.screen).join(' -> ')
        );
        console.log(`NAVIGATION_DEBUG: Previous screen params:`, prevNavigation.params);

        // Update current screen and parameters
        setCurrentScreen(prevNavigation.screen);
        setScreenParams(prevNavigation.params);
        sessionStorage.setItem('nav_current_screen', prevNavigation.screen);

        // Remove the last item from history
        setHistory(history.slice(0, -1));
      } else {
        // If no history, go to default screen
        console.log(
          `NAVIGATION_DEBUG: No history available, going to default screen ${defaultScreen}`
        );
        console.log(`NAVIGATION_DEBUG: Current screen before default navigation: ${currentScreen}`);
        console.log(`NAVIGATION_DEBUG: Current params before default navigation:`, screenParams);

        setCurrentScreen(defaultScreen);
        setScreenParams({});
        sessionStorage.setItem('nav_current_screen', defaultScreen);
      }
    },
    [getUserHistory, isProfileUser, currentScreen, screenParams]
  );

  // Check if there's navigation history
  const hasHistory = useCallback(() => {
    const { history } = getUserHistory();
    return history.length > 0;
  }, [getUserHistory]);

  // Get current screen params
  const getScreenParams = useCallback(() => {
    return screenParams;
  }, [screenParams]);

  // QR Overlay controls
  const showQR = useCallback(() => {
    console.log('NAVIGATION: showQR called — opening QR overlay');
    setIsQROpen(true);
    sessionStorage.setItem('nav_qr_open', 'true');
  }, []);

  const hideQR = useCallback(() => {
    console.log('NAVIGATION: hideQR called — closing QR overlay');
    setIsQROpen(false);
    sessionStorage.removeItem('nav_qr_open');
  }, []);

  // Push notification tap-to-open listener
  const { updateSessionChatRoomId } = useSession();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.chatRoomId) {
        // Write to sessionStorage synchronously so ProfileConnectionPage
        // can read the chatRoomId on mount (React state update is async)
        sessionStorage.setItem('sessionChatRoomId', event.data.chatRoomId);
        updateSessionChatRoomId(event.data.chatRoomId);
        navigateTo('profile-connection-page', { source: 'notification' });
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, [navigateTo, updateSessionChatRoomId]);

  // PERFORMANCE: Memoize context value to prevent consumer re-renders
  // Without this, every NavigationProvider render creates a new object,
  // which causes all useNavigation() consumers to re-render
  const contextValue = useMemo(
    () => ({
      currentScreen,
      screenParams,
      navigateTo,
      goBack,
      hasHistory,
      getScreenParams,
      isQROpen,
      showQR,
      hideQR,
    }),
    [
      currentScreen,
      screenParams,
      navigateTo,
      goBack,
      hasHistory,
      getScreenParams,
      isQROpen,
      showQR,
      hideQR,
    ]
  );

  // Provide the navigation context to children
  return <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>;
};

/**
 * Custom hook to use the navigation context
 */
export const useNavigation = () => {
  const context = useContext(NavigationContext);

  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }

  return context;
};
